import { api, ApiError } from './api';
import { ValidationIssue } from './deliveryConfigApi';
import {
  CursorPage,
  PromoCodeCreateRequest,
  PromoCodeDetail,
  PromoCodeSummary,
  PromoCodeUpdateRequest,
  PromoCustomer,
  PromoHolderRow,
  PromoHolderStatus,
  PromoListQuery,
  PromoSettings,
  ShareRecipient,
  ShareResponse,
} from '../types/promotions';

const BASE = '/api/admin/promotions';
const enc = encodeURIComponent;

function query(params: Record<string, string | number | null | undefined>): string {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

/**
 * Spec 059 — admin promo codes, served by **orders** (gateway route `orders-admin-promotions`,
 * screen `promotions`). `settings` is the one sub-path gated on another screen: `finance-config`
 * since #1113 (was `finance`; gateway `/api/admin/promotions/settings/**` → finance-config), so a
 * promotions-only admin gets 403 there and callers must treat the cap as optional.
 */
export const promotionsApi = {
  list: (params: PromoListQuery, token: string) =>
    api.get<CursorPage<PromoCodeSummary>>(`${BASE}${query({ ...params })}`, token),

  /** 201 · 409 PROMO_CODE_DUPLICATE · 422 PROMO_VALUE_EXCEEDS_CAP | PROMO_FIXED_OUT_OF_RANGE | … */
  create: (input: PromoCodeCreateRequest, token: string) =>
    api.post<PromoCodeDetail>(BASE, input, token),

  get: (id: string, token: string) =>
    api.get<PromoCodeDetail>(`${BASE}/${enc(id)}`, token),

  /** 409 PROMO_VERSION_CONFLICT | PROMO_TERMS_LOCKED · 422 PROMO_LIMIT_BELOW_USAGE */
  update: (id: string, input: PromoCodeUpdateRequest, token: string) =>
    api.put<PromoCodeDetail>(`${BASE}/${enc(id)}`, input, token),

  disable: (id: string, token: string) =>
    api.post<PromoCodeDetail>(`${BASE}/${enc(id)}/disable`, {}, token),

  enable: (id: string, token: string) =>
    api.post<PromoCodeDetail>(`${BASE}/${enc(id)}/enable`, {}, token),

  holders: (id: string, params: { status?: PromoHolderStatus; cursor?: string; size?: number }, token: string) =>
    api.get<CursorPage<PromoHolderRow>>(`${BASE}/${enc(id)}/holders${query({ ...params })}`, token),

  /** 200 per-recipient results · 409 PROMO_NOT_SHAREABLE · 422 when every recipient is invalid. */
  share: (id: string, recipients: ShareRecipient[], token: string) =>
    api.post<ShareResponse>(`${BASE}/${enc(id)}/shares`, { recipients }, token),

  /** 409 PROMO_HOLDER_USED once the holder has used the code or a booking holds a use. */
  revoke: (id: string, holderId: string, token: string) =>
    api.post<unknown>(`${BASE}/${enc(id)}/holders/${enc(holderId)}/revoke`, {}, token),

  /** 202 — same holder, no new slot. */
  resend: (id: string, messageId: string, token: string) =>
    api.post<unknown>(`${BASE}/${enc(id)}/messages/${enc(messageId)}/resend`, {}, token),

  searchCustomers: (search: string, token: string, cursor?: string, size = 20) =>
    api.get<CursorPage<PromoCustomer>>(`${BASE}/customers${query({ search, cursor, size })}`, token),

  /** The cap only — readable on the promotions screen (settings itself is finance-config-gated, #1113). */
  getCap: (token: string) =>
    api.get<{ maxDiscountPercent: number }>(`${BASE}/cap`, token),

  getSettings: (token: string) =>
    api.get<PromoSettings>(`${BASE}/settings`, token),

  /** 422 PROMO_CAP_OUT_OF_RANGE outside whole 1–100. */
  updateSettings: (maxDiscountPercent: number, token: string) =>
    api.put<PromoSettings>(`${BASE}/settings`, { maxDiscountPercent }, token),
};

export const VERSION_CONFLICT_MESSAGE = 'This code was changed by someone else. Reload.';
export const TERMS_LOCKED_MESSAGE = 'Create a new code to change the terms.';

/** BR-10 — someone saved the code since this form loaded it. */
export function isVersionConflict(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409
    && (err.code === 'PROMO_VERSION_CONFLICT' || /changed by someone else/i.test(err.message));
}

/** Business codes (design §3) → the form field an admin goes and fixes; null = form-level. */
const CODE_FIELDS: Record<string, string | null> = {
  PROMO_CODE_DUPLICATE: 'code',
  PROMO_VALUE_EXCEEDS_CAP: 'discountValue',
  PROMO_FIXED_OUT_OF_RANGE: 'discountValue',
  PROMO_MAX_PER_USE_PERCENT_ONLY: 'maxDiscountPerUse',
  PROMO_WINDOW_INVALID: 'expiresAt',
  PROMO_CAP_OUT_OF_RANGE: 'maxDiscountPercent',
  PROMO_TERMS_LOCKED: null,
  PROMO_VERSION_CONFLICT: null,
  PROMO_ARCHIVED: null,
};

function fieldForPromoMessage(message: string, details?: Record<string, unknown>): string | null {
  if (details && typeof details.field === 'string') return details.field;
  const m = message.toLowerCase();
  if (m.includes('already exists') || m.startsWith('code')) return 'code';
  if (m.includes('percentage') || m.includes('between aed')) return 'discountValue';
  if (m.includes('max per use')) return 'maxDiscountPerUse';
  if (m.includes('expiry')) return 'expiresAt';
  if (m.includes('maximum discount')) return 'maxDiscountPercent';
  if (m.includes('users') || m.includes('holders')) return 'maxHolders';
  if (m.includes('uses')) return 'usesPerHolder';
  return null;
}

/**
 * Where a rejected promo save belongs on screen. Same contract as `deliveryConfigApi`'s
 * `toValidationIssues`, plus 409 (duplicate code, version conflict, locked terms) and the
 * `{ code, message, details }` body: anything that is not a 400/409/422 returns `[]` so the caller
 * shows it as an error banner/toast with the form values kept (AC-68).
 */
export function toPromoIssues(err: unknown): ValidationIssue[] {
  if (!(err instanceof ApiError)) return [];
  if (err.status !== 400 && err.status !== 409 && err.status !== 422) return [];

  if (isVersionConflict(err)) return [{ field: null, message: VERSION_CONFLICT_MESSAGE }];
  if (err.code === 'PROMO_TERMS_LOCKED') return [{ field: null, message: TERMS_LOCKED_MESSAGE }];

  const flattened = /\(([^()]*:[^()]*)\)\s*$/.exec(err.message);
  if (flattened) {
    const issues = flattened[1].split(';').map((part) => part.trim()).filter(Boolean).map((part) => {
      const at = part.indexOf(':');
      return { field: part.slice(0, at).trim(), message: part.slice(at + 1).trim() };
    }).filter((i) => i.field && i.message);
    if (issues.length > 0) return issues;
  }

  if (err.code === 'PROMO_LIMIT_BELOW_USAGE') {
    return [{ field: fieldForPromoMessage(err.message, err.details) ?? 'maxHolders', message: err.message }];
  }
  if (err.code && err.code in CODE_FIELDS) return [{ field: CODE_FIELDS[err.code], message: err.message }];
  return [{ field: fieldForPromoMessage(err.message, err.details), message: err.message }];
}
